import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import PickCard from "@/components/PickCard";
import Disclaimer from "@/components/Disclaimer";
import { BET_LABEL, bettingDayBounds, todayBettingDatePT, nextBettingDatePT } from "@/lib/picks";
import { fetchPickAccessMap } from "@/lib/access";
import { ROLLOVER_HINT } from "@/lib/slate";
import { Trophy, Sparkles, ShieldCheck, Lightbulb, Clock, Sunrise } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import PicksTabs from "@/components/PicksTabs";
import SegmentedToggle from "@/components/SegmentedToggle";
import { format } from "date-fns";
import Seo from "@/components/Seo";

const RANK_REASON: Record<number, string> = {
  1: "Highest projected edge today",
  2: "Strongest market inefficiency",
  3: "Top model confidence",
  4: "High-conviction value play",
  5: "Notable model lean",
};

export default function Top5() {
  const { user } = useAuth();
  const [todayPicks, setTodayPicks] = useState<any[]>([]);
  const [tomorrowPicks, setTomorrowPicks] = useState<any[]>([]);
  const [slate, setSlate] = useState<"today" | "tomorrow">("today");
  const [day, setDay] = useState<{ start: string; end: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);

  useEffect(() => {
    if (!user) return;
    (async () => {
      setLoading(true);
      const bounds = bettingDayBounds();
      const bettingDate = todayBettingDatePT();
      const tomorrowDate = nextBettingDatePT();
      setDay(bounds);
      const fetchSlate = async (bd: string) => {
        const { data } = await supabase
          .from("picks")
          .select("*, sports(code)")
          .eq("status", "published")
          .eq("is_top5", true)
          .is("deleted_at", null)
          .filter("betting_date", "eq", bd)
          .order("confidence_score", { ascending: false })
          .limit(5);
        const ids = (data ?? []).map((p) => p.id);
        const accessMap = await fetchPickAccessMap(user.id, bounds.start, new Date(Date.now() + 48 * 3600 * 1000).toISOString(), false, true, ids);
        return (data ?? []).map((p) => ({ ...p, access: accessMap.get(p.id) }));
      };
      const [today, tomorrow] = await Promise.all([fetchSlate(bettingDate), fetchSlate(tomorrowDate)]);
      setTodayPicks(today);
      setTomorrowPicks(tomorrow);
      if (today.length === 0 && tomorrow.length > 0) setSlate("tomorrow");
      setRefreshedAt(new Date());
      setLoading(false);
    })();
  }, [user?.id]);

  const picks = slate === "tomorrow" ? tomorrowPicks : todayPicks;
  const showToggle = todayPicks.length > 0 && tomorrowPicks.length > 0;
  const headingPossessive = slate === "tomorrow" ? "Tomorrow's" : "Today's";


  // Daily editorial framing
  const editorial = useMemo(() => {
    if (picks.length < 2) return null;
    const groups: Record<string, { count: number; conf: number }> = {};
    picks.forEach((p) => {
      const key = `${p.sports?.code ?? "—"} ${BET_LABEL[p.bet_type] ?? p.bet_type}`;
      groups[key] = groups[key] ?? { count: 0, conf: 0 };
      groups[key].count++;
      groups[key].conf += Number(p.confidence_score ?? 0);
    });
    const top = Object.entries(groups)
      .map(([k, v]) => ({ k, count: v.count, avg: v.conf / v.count }))
      .sort((a, b) => b.count * b.avg - a.count * a.avg)[0];
    if (!top) return null;
    return `Tonight's strongest model edges are concentrated in ${top.k.toLowerCase()} — ${top.count} of today's Top 5 plays.`;
  }, [picks]);

  const freeIdx = picks.findIndex((p) => p.access?.is_free_pick);
  const featured = freeIdx >= 0 ? picks[freeIdx] : picks[0];
  const rest = picks.filter((_, i) => i !== (freeIdx >= 0 ? freeIdx : 0));
  const rankMap = useMemo(() => {
    const ordered = freeIdx > 0 ? [picks[freeIdx], ...picks.filter((_, i) => i !== freeIdx)] : picks;
    const map = new Map<string, number>();
    ordered.forEach((p, i) => map.set(p.id, i + 1));
    return map;
  }, [picks, freeIdx]);

  return (
    <div className="container py-8 md:py-12">
      <Seo
        title="Top 5 AI Sports Picks Today | AleaEdge"
        description="Today's five highest-conviction AI sports picks with full reasoning, odds and tracked results."
        path="/top-5"
      />
      {/* Hero */}
      <div className="flex flex-col gap-5">
        <PicksTabs className="self-start md:self-center" />

        <div className="text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full border border-accent/30 bg-accent/10 px-3 py-1 text-[11px] uppercase tracking-[0.2em] text-accent font-semibold">
            <Trophy className="h-3.5 w-3.5" /> {headingPossessive} Best
          </div>
          <h1 className="font-display text-3xl md:text-5xl font-bold mt-4 leading-tight">
            {headingPossessive} Top 5 <span className="te-grad-text">AI Plays</span>
          </h1>
          <p className="text-muted-foreground mt-3 text-sm md:text-base">
            Curated daily from hundreds of model evaluations · ranked by projected edge.
          </p>
          {showToggle && (
            <div className="mt-4 flex justify-center">
              <SegmentedToggle
                ariaLabel="Slate"
                options={[
                  { value: "today", label: "Today" },
                  { value: "tomorrow", label: "Tomorrow", icon: Sunrise },
                ]}
                value={slate}
                onChange={(v) => setSlate(v as "today" | "tomorrow")}
              />
            </div>
          )}

          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[11px] text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-3 w-3" />
              {day ? new Date(day.start).toLocaleDateString("en-US", { timeZone: "America/Los_Angeles", weekday: "long", month: "long", day: "numeric" }) : "—"} · PT
            </span>
            {refreshedAt && (
              <span className="inline-flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-success animate-pulse" />
                Updated {format(refreshedAt, "h:mm a")}
              </span>
            )}
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="h-3 w-3 text-success" />
              Tracked transparently in History
            </span>
          </div>
          <p className="mt-2 text-[10px] uppercase tracking-wider text-muted-foreground/70">{ROLLOVER_HINT}</p>
        </div>

        {editorial && !loading && (
          <div className="mx-auto max-w-2xl flex items-center gap-2.5 rounded-2xl border border-border/50 bg-secondary/30 px-4 py-3 text-xs md:text-sm text-muted-foreground">
            <Lightbulb className="h-4 w-4 text-accent shrink-0" />
            <span>{editorial}</span>
          </div>
        )}
      </div>

      {/* Picks */}
      <div className="mt-10 md:mt-12">
        {loading ? (
          <div className="space-y-6">
            <Skeleton className="h-[340px] rounded-2xl" />
            <div className="grid md:grid-cols-2 gap-5">
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-[280px] rounded-2xl" />)}
            </div>
          </div>
        ) : picks.length === 0 ? (
          <div className="te-card p-12 text-center">
            <div className="mx-auto h-12 w-12 rounded-full bg-secondary/60 grid place-items-center mb-3">
              <Trophy className="h-5 w-5 text-muted-foreground/70" />
            </div>
            <div className="font-medium">{headingPossessive} Top 5 isn't published yet.</div>
            <div className="text-xs text-muted-foreground mt-1">Our model is still evaluating the board — check back soon.</div>
          </div>
        ) : (
          <div className="space-y-6">
            {featured && (
              <div className="relative">
                <div className="absolute -inset-x-4 -top-4 h-44 bg-gradient-to-b from-primary/10 via-primary/0 to-transparent blur-2xl rounded-full pointer-events-none -z-10" />
                <div className="mb-3 flex items-center justify-between gap-3 flex-wrap">
                  <div className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/15 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-primary">
                    <Sparkles className="h-3 w-3" /> Today's Free Pick
                  </div>
                  <div className="text-[11px] text-muted-foreground hidden sm:block">Featured play of the day</div>
                </div>
                <div className="rounded-2xl ring-1 ring-primary/20 shadow-elevated">
                  <PickCard
                    pick={featured}
                    sportCode={featured.sports?.code ?? "NBA"}
                    rank={rankMap.get(featured.id) ?? 1}
                    locked={featured.access?.is_locked ?? true}
                  />
                </div>
              </div>
            )}

            {rest.length > 0 && (
              <div className="grid md:grid-cols-2 gap-4 md:gap-5">
                {rest.map((p) => (
                  <div key={p.id} className="flex flex-col">
                    <PickCard
                      pick={p}
                      sportCode={p.sports?.code ?? "NBA"}
                      rank={rankMap.get(p.id) ?? 1}
                      locked={p.access?.is_locked ?? true}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mt-12"><Disclaimer /></div>
    </div>
  );
}
